import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient, HttpResponse } from '@angular/common/http';
import { AdjustmentUi } from '../../report/adjustments/models/adjustment-ui.model';
import { Application } from '../../settings/enums/application.enum';
import { AuthService } from '../../auth/services/auth.service';
import { DatasetDate } from '../../report/models/dataset.model';
import { Injectable } from '@angular/core';
import { ReportResponse } from '../models/report-response.model';
import { RoutingService } from '../../routing/services/routing.service';
import { SettingsService } from '../../settings/services/settings.service';
import { SpecificationUi } from '../../report/adjustments/models/specification-ui.model';

import 'rxjs/Rx';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';

@Injectable()
export class ReportService {
  private fleetAppName = 'bv_fleet';
  application: Application;

  appSettings = this.settingsService.Get();
  onProjectListUpdate = new Subject<ReportResponse[]>();
  onReportSummaryUpdate = new Subject<ReportResponse[]>();
  onDataSetsLoaded = new Subject<any>();
  apiUrl = this.appSettings.apiUrl;

  applicationTerminology: string;

  isCreatingReport = false;
  datasets: Array<DatasetDate>;
  reports: Array<ReportResponse>;
  reportSummary: Array<ReportResponse>;
  dataSetsLoaded: boolean = false;

  isReportRefreshInProgress = false;

  private calledForDatasets = false;

  constructor(private httpService: HttpClient, private routingService: RoutingService,
                  private authService: AuthService, private settingsService: SettingsService) {
    if (this.appSettings.productName === this.fleetAppName) {
      this.application = Application.fleet;
      this.applicationTerminology = 'Contract';
    } else {
      this.application = Application.finance;
      this.applicationTerminology = 'Agreement';
    }

  }

  getLatestReports() {
    this.getReportDataFromService();
  }

  getAllReportSummarys(){
    this.getReportSummaryFromService(false);
  }

  getEndPointUrl = (currentPageOnly: boolean = true) => {
    var currentPage = 1; // always 1 until UI supports multiple pages;
    return `${this.apiUrl}/reports/${currentPageOnly ? this.appSettings.reportPageSize + '/1' : ''}`;
  }

  handleInitialPageDataLoad() {
    let promise = new Promise((resolve, reject) => {
      this.loadDataSets()
        .toPromise()
        .then(
            (response: any) => {
              this.datasets = response;
              this.onDataSetsLoaded.next(this.datasets);
              this.calledForDatasets = false;
              resolve();
            }
        );
    });
    return promise;
  }

  getReportSummaryFromService(currentPageOnly: boolean = true) {
    this.isReportRefreshInProgress = true;
    if (this.authService.getAuthorisationHeader() == null) {
        return;
    }
      this.getReports(currentPageOnly)
        .toPromise()
        .then(
            (response: ReportResponse[]) => {
              this.reportSummary = response as ReportResponse[];
              this.isReportRefreshInProgress = false
              this.onReportSummaryUpdate.next(this.reportSummary);
            }
        ).catch(() => {
          this.isReportRefreshInProgress = false;
        });
  }

  getReportDataFromService(currentPageOnly: boolean = true) {
    this.isReportRefreshInProgress = true;
    if (this.authService.getAuthorisationHeader() == null) {
        return;
    }
      this.getReports(currentPageOnly)
        .toPromise()
        .then(
            (response: ReportResponse[]) => {
              this.reports = response as ReportResponse[];
              this.isReportRefreshInProgress = false
              this.onProjectListUpdate.next(this.reports);
            }
        ).catch(() => {
          this.isReportRefreshInProgress = false;
        });
  }

  public updateReportProgressFromService() {
    let progress = Observable.interval(this.appSettings.reportRefreshInterval)
      .switchMap(() => this.getReports())
      .subscribe(
      (response: ReportResponse[]) => {
        this.reports = response as ReportResponse[];
        this.onProjectListUpdate.next(this.reports);
        var inProgressReports = this.reports.filter(r => r.report.reportStatusId === 0).length;
        if (inProgressReports === 0) { // all reports completed
          progress.unsubscribe();
          console.log('finished!!!')
        }
        else {
          console.log('In progress.....!!!')
        }
      },
      error => {
        console.log(error);
      });
  }

  private loadDataSets() {
    if (this.authService.getAuthorisationHeader() == null) {
        return Observable.of(new Response(null));
    }
    return this.httpService.get(this.apiUrl + '/settings/datasets', { headers: this.authService.getAuthorisationHeader() });
  }


  private getReports(currentPageOnly: boolean = true) {
    if (this.authService.getAuthorisationHeader() == null) {
        return Observable.of(new Response(null));
    }
    return this.httpService.get(this.getEndPointUrl(currentPageOnly), { headers: this.authService.getAuthorisationHeader() });
  }

  getDatasetDates() {
    if (this.datasets != null) {
      this.onDataSetsLoaded.next(this.datasets);
    } else {
      console.log('data sets are null in report service');
    }
  }

  archiveReports(reportsToArchive: ReportResponse[]) {
    const reportIdArray: Array<string> = new Array<string>();

    for (let i = 0; i < reportsToArchive.length; i++) {
      reportIdArray.push(reportsToArchive[i].report.id.toString());
    }
    let requestHeader = this.authService.getAuthorisationHeader();
    requestHeader = requestHeader.set('content-type', 'application/json');
    return this.httpService.post(this.apiUrl + '/reports/archive', JSON.stringify(reportIdArray), { headers: requestHeader });
  }

  restoreReports(reportsToRestore: ReportResponse[]) {
    const reportIdArray: Array<string> = new Array<string>();

    for (let i = 0; i < reportsToRestore.length; i++) {
      reportIdArray.push(reportsToRestore[i].report.id.toString());
    }
    let requestHeader = this.authService.getAuthorisationHeader();
    requestHeader = requestHeader.set('content-type', 'application/json');
    return this.httpService.post(this.apiUrl + '/reports/restore', JSON.stringify(reportIdArray), { headers: requestHeader });
  }

  renameReport(report: ReportResponse) {
    let requestHeader = this.authService.getAuthorisationHeader();
    requestHeader = requestHeader.set('content-type', 'application/json');
    return this.httpService.post(this.apiUrl + '/reports/rename', JSON.stringify(report.report), { headers: requestHeader });
  }

  deleteReports(reportsToDelete: ReportResponse[]) {
    const reportIdArray: Array<string> = new Array<string>();

    for (let i = 0; i < reportsToDelete.length; i++) {
      reportIdArray.push(reportsToDelete[i].report.id.toString());
    }
    let requestHeader = this.authService.getAuthorisationHeader();
    requestHeader = requestHeader.set('content-type', 'application/json');
    return this.httpService.post(this.apiUrl + '/reports/delete', JSON.stringify(reportIdArray), { headers: requestHeader });
  }

  getAllManufacturers() {
    return this.httpService.get(this.apiUrl + '/manufacturers', { headers: this.authService.getAuthorisationHeader() })
      .map((response) => response as Array<AdjustmentUi>);
  }

  getAllSectors() {
    return this.httpService.get(this.apiUrl + '/sectors', { headers: this.authService.getAuthorisationHeader() })
      .map((response) => response as Array<AdjustmentUi>);
  }

  getAllSpecifications() {
    return this.httpService.get(this.apiUrl + '/specification', { headers: this.authService.getAuthorisationHeader() })
      .map((response) => response as Array<SpecificationUi>);
  }

  getReportSummary(reportId: number, callBack: (summary) => void) {
    const matchingReport = this.getMatchingReport(reportId);
    if (matchingReport) {
      callBack(matchingReport.reportSummary);
    }
  }
  getReportDiscrepancies(reportId: number) {
    const matchingReport = this.getMatchingReport(reportId);
    if (matchingReport) {
      return matchingReport.discrepancies;
    }
  }

  getReport(reportId: number) {
    const matchingReport = this.getMatchingReport(reportId);
    if (matchingReport) {
      return matchingReport.report;
    }
  }

  private getMatchingReport(reportId: number) {
    if (this.reports) {
      return this.matchReport(reportId);
    }
  }

  private matchReport(reportId: number) {
    const matchingReports = this.reports.filter(r => r.report.id == reportId);
    if (matchingReports.length === 1) {
      return matchingReports[0];
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/report/services/report.service.ts